import { _ } from "../../core/polyglot/polyglot.js";
import Model from "../Model.js";
import SingleAccountSpecification from "./SingleAccountSpecification.js";
import specificationFactory from "../specification/specificationFactory.js";

/**
 * Configuration necessary to calculate CashConversion residuals.
 *
 * @param {number[]} [residualsSpread] - The residuals spread is the
 *   distribution of the AP/AR to the cash account.
 *   For example, 20% Month +1 / 50% Month +2 / 30% Month +3 would be modelized by
 *   [20, 50, 30]. The sum of the elements must be 100. Default value is an
 *   array with one element equal to 100
 * @param {AbstractSpecification} [residualAccountSpecification] - Account on
 *   which the residuals are calculated on. Default value is a
 *   SingleAccountSpecification with an empty string as account number
 */
export default class CashConversionConfiguration extends Model {
	constructor({
		residualsSpread = [0],
		residualAccountSpecification = new SingleAccountSpecification(),
	} = {}) {
		super(...arguments);
		this._residualsSpread = residualsSpread;
		this._residualAccountSpecification = residualAccountSpecification;
	}

	getResidualsSpread() {
		return this._residualsSpread;
	}

	setResidualsSpread(value) {
		this._residualsSpread = value;
	}

	getResidualAccountSpecification() {
		return this._residualAccountSpecification;
	}

	setResidualAccountSpecification(value) {
		this._residualAccountSpecification = value;
	}

	validate({ collector }) {
		const sumOfValues = this._residualsSpread.reduce(
			(result, currentValue) => result + currentValue,
			0,
		);

		collector.addErrorIf(
			sumOfValues !== 100,
			_("Residuals Spread should add up to 100"),
		);
	}

	_serializedProperties() {
		return {
			residualsSpread: this._residualsSpread,
			residualAccountSpecification: this._residualAccountSpecification,
		};
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(data, context);

		data.residualAccountSpecification &&= specificationFactory.materialize(
			data.residualAccountSpecification,
		);
	}
}
